import { Icon } from "@/components/icons";
import { cn } from "cn";
import { type VariantProps, cva } from "class-variance-authority";
import type * as React from "react";

const selectVariants = cva(
  "flex items-center justify-between rounded-md shadow-sm hover:bg-neutral-100 transition-colors bg-white dark:bg-slate-950 dark:hover:bg-neutral-800 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 ring-offset-background outline-none ring-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none aria-invalid:shadow-[0_0_0_1px_var(--color-destructive)] aria-invalid:focus-visible:ring-red-500/40",
  {
    variants: {
      size: {
        sm: "h-7 pl-3 pr-6 text-xs font-medium",
        md: "h-9 pl-3 pr-8 py-2 text-sm shadow-sm",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

const iconVariants = cva("", {
  variants: {
    size: {
      sm: "size-5",
      md: "size-6",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

type SelectVariantProps = VariantProps<typeof selectVariants>;

interface SelectNativeProps
  extends Omit<React.ComponentProps<"select">, "size"> {
  size?: SelectVariantProps["size"];
}

const SelectNative = ({
  className,
  children,
  size,
  ...props
}: SelectNativeProps) => {
  return (
    <span className="relative inline-flex w-fit">
      <select
        data-slot="select-native"
        className={cn(
          selectVariants({ size }),
          props.multiple
            ? "[&_option:checked]:bg-neutral-100 py-1 *:px-3 *:py-1"
            : "",
          "field-sizing-content",
          className,
        )}
        {...props}
      >
        {children}
      </select>
      {!props.multiple && (
        <span className="absolute right-1 flex h-full items-center justify-center text-neutral-500 dark:text-neutral-400 pointer-events-none">
          <Icon name="ChevronDown" className={iconVariants({ size })} />
        </span>
      )}
    </span>
  );
};

export { SelectNative, type SelectNativeProps, selectVariants, iconVariants };
